import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getUser, sendTestReport } from './api';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import PlatformPage from './pages/PlatformPage';
import AdminActivityPage from './pages/AdminActivityPage';
import CampaignsPage from './pages/CampaignsPage';
import InfluencerProfile from './pages/InfluencerProfile';
import PublicInfluencerForm from './pages/PublicInfluencerForm';

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Montserrat', -apple-system, sans-serif;
    background: #1A1A1A;
    color: #F5EBDD;
    -webkit-font-smoothing: antialiased;
  }

  /* Sheet scroll container */
  .sheet-scroll {
    overflow-x: scroll;
    overflow-y: visible;
    scrollbar-width: none;
  }
  .sheet-scroll::-webkit-scrollbar { height: 0px; width: 0px; }

  /* Sticky headers — stick to top of scroll container */
  .sheet-scroll thead th {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  /* Sticky name column — stick to left when scrolling right */
  .sheet-scroll td.col-name {
    position: sticky;
    left: 0;
    z-index: 2;
    background: #1A1A1A;
    box-shadow: 3px 0 8px rgba(0,0,0,0.6);
  }
  .sheet-scroll thead th.col-name {
    left: 0;
    z-index: 12;
    box-shadow: 3px 0 8px rgba(0,0,0,0.6);
  }

  /* Sticky scrollbar bar at bottom of viewport */
  .sheet-sticky-scroll {
    position: sticky;
    bottom: 0;
    overflow-x: scroll;
    overflow-y: hidden;
    height: 12px;
    margin: 0 2rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(229,93,74,0.7) rgba(245,235,221,0.06);
    background: rgba(26,26,26,0.95);
    z-index: 50;
  }
  .sheet-sticky-scroll::-webkit-scrollbar { height: 10px; }
  .sheet-sticky-scroll::-webkit-scrollbar-track { background: rgba(245,235,221,0.06); border-radius: 4px; }
  .sheet-sticky-scroll::-webkit-scrollbar-thumb { background: rgba(229,93,74,0.7); border-radius: 4px; }
  .sheet-sticky-scroll::-webkit-scrollbar-thumb:hover { background: rgba(229,93,74,1); }
  .sheet-sticky-inner { height: 1px; }

  /* Input focus glow */
  input:focus, select:focus, textarea:focus {
    border-color: #E55D4A !important;
    box-shadow: 0 0 0 2px rgba(229,93,74,0.12);
  }

  /* Selection color */
  ::selection { background: rgba(216,224,77,0.25); color: #F5EBDD; }

  /* Smooth transitions */
  button { transition: opacity 0.15s, background 0.15s; }
  button:hover { opacity: 0.88; }
`;

function PrivateRoute({ children }) {
  return getUser() ? (
    <div style={{ minHeight:'100vh', background:'#1A1A1A', color:'#F5EBDD', fontFamily:"'Montserrat', sans-serif" }}>
      <Navbar />
      {children}
    </div>
  ) : <Navigate to="/login" replace />;
}

export default function App() {

  // Auto-send daily report at 6pm Mon–Fri
  useEffect(() => {
    const user = getUser();
    if (!user || user.role !== 'admin') return;

    function checkAndSend() {
      const now    = new Date();
      const day    = now.getDay(); // 0=Sun, 1=Mon...5=Fri, 6=Sat
      const hour   = now.getHours();
      const today  = now.toISOString().slice(0, 10);
      const key    = `chord_report_sent_${today}`;

      // Only Mon–Fri (1–5), at or after 18:00
      if (day === 0 || day === 6) return;
      if (hour < 18) return;

      // Already sent today
      if (localStorage.getItem(key)) return;

      // Mark sent and fire
      localStorage.setItem(key, '1');
      sendTestReport(7).catch(() => {
        // If it fails, remove the flag so it can retry
        localStorage.removeItem(key);
      });
    }

    // Check immediately on load
    checkAndSend();

    // Also check every 10 minutes (in case app is left open)
    const interval = setInterval(checkAndSend, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BrowserRouter>
      <style>{globalCSS}</style>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/join" element={<PublicInfluencerForm />} />
        <Route path="/" element={<PrivateRoute><HomePage /></PrivateRoute>} />
        <Route path="/activity"   element={<PrivateRoute><AdminActivityPage /></PrivateRoute>} />
        <Route path="/campaigns"  element={<PrivateRoute><CampaignsPage /></PrivateRoute>} />
        <Route path="/platform/:platformKey" element={<PrivateRoute><PlatformPage /></PrivateRoute>} />
        <Route path="/platform/:platformKey/influencer/:id" element={<PrivateRoute><InfluencerProfile /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
